import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/**
 * The organization's firm onboarding defaults: its own copy of the saved proposal template that a
 * newly rolled-out firm's draft proposal is built from.
 *
 * A read model with one write, like `VendorRolloutBoardSchema`: the API resolves the record from
 * the bearer token and there is no id to carry, so nothing here is constructed in the browser as a
 * domain object. It is a parsed shape and the request that changes it.
 */

/** The gaps the API distinguishes. Mirrors `ClientProposalTemplateGap`. */
export const CLIENT_PROPOSAL_TEMPLATE_GAPS = [
  "NotConfigured",
  "Disabled",
  "NoProposalTemplate",
  "NoFeeSchedule",
  "NoTeam",
] as const;

/**
 * What a gap name this bundle does not recognize becomes.
 *
 * Only the name degrades. `gapReason` is prose the server wrote for a reader and is printed
 * whatever the name beside it says, which is the whole reason both travel: a client holding a
 * bundle that predates a sixth gap still reads the sentence telling them what to go and fix.
 */
export const UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP = "Unrecognized";

export type ClientProposalTemplateGap =
  | (typeof CLIENT_PROPOSAL_TEMPLATE_GAPS)[number]
  | typeof UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP;

const degradeUnrecognizedGap = ({ value }: { value: unknown }): ClientProposalTemplateGap => {
  logSchemaFallback({
    schemaName: "ClientProposalTemplateSchema",
    field: "gap",
    value,
    fallback: UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP,
  });
  return UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP;
};

const ClientProposalTemplateGapZ = z.enum([
  ...CLIENT_PROPOSAL_TEMPLATE_GAPS,
  UNRECOGNIZED_CLIENT_PROPOSAL_TEMPLATE_GAP,
]);

/**
 * The copy of a saved proposal template the defaults hold, summarized.
 *
 * A copy rather than a reference: it is taken when the template is picked, so editing or deleting
 * the saved template afterwards changes nothing here. `sourceProposalTemplateId` and `sourceName`
 * say where it came from at that moment. The API sends counts rather than the rows they count,
 * because this page summarizes the copy and edits none of it. `discount` is a fraction between 0
 * and 1.
 */
const CopiedProposalTemplateJSONZ = z.object({
  sourceProposalTemplateId: z.uuid().nullish(),
  sourceName: z.string(),
  // With or without an offset: which one a .NET date serializes with depends on its Kind, and
  // refusing one would throw the whole record away over the line that says when it was copied.
  copiedDate: z.iso.datetime({ offset: true, local: true }),
  copiedByUserId: z.uuid(),
  proposalName: z.string(),
  description: z.string(),
  negotiable: z.boolean(),
  conflictsCheckWaived: z.boolean(),
  discount: z.number(),
  teamLeaderUserId: z.uuid().nullish(),
  /** The members other than the leader. */
  teamMemberCount: z.number().int(),
  feeCategoryCount: z.number().int(),
  conflictsDocumentCount: z.number().int(),
  policyDocumentCount: z.number().int(),
});

export type CopiedProposalTemplateJSON = z.infer<typeof CopiedProposalTemplateJSONZ>;

/** The defaults as the API sends them. */
const ClientProposalTemplateJSONZ = z.object({
  clientLegalEntityId: z.uuid(),
  /** Null until a template has been picked. */
  proposalTemplate: CopiedProposalTemplateJSONZ.nullish(),
  autoCreateEnabled: z.boolean(),
  isReadyForAutoCreation: z.boolean(),
  gap: ClientProposalTemplateGapZ.catch(degradeUnrecognizedGap).nullish(),
  /** Why nothing is being built, in the words the roll-out records against the firm. */
  gapReason: z.string().nullish(),
});

export type ClientProposalTemplateJSON = z.infer<typeof ClientProposalTemplateJSONZ>;

/**
 * What a save sends.
 *
 * `copyFromProposalTemplateId` names a saved template to copy now, replacing the copy held. Null
 * keeps the copy as it is and changes only the switch: copying again on every save would silently
 * pick up edits to the saved template that nobody chose to apply here.
 */
export type UpdateClientProposalTemplateRequest = {
  copyFromProposalTemplateId: string | null;
  autoCreateEnabled: boolean;
};

export default class ClientProposalTemplateSchema {
  public static readonly ClientProposalTemplateJSONZ = ClientProposalTemplateJSONZ;

  public static validateJSON(json: unknown): ClientProposalTemplateJSON {
    return parseOrThrow(ClientProposalTemplateJSONZ, json, {
      logError: true,
      schemaName: "ClientProposalTemplateSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is ClientProposalTemplateJSON {
    return ClientProposalTemplateJSONZ.safeParse(json).success;
  }
}
